/**
 * 뉴스레터 콘텐츠 레지스트리.
 *
 * 두 가지 소스를 하나의 목록으로 합친다.
 *
 *  1. 정적 페이지 (기본)
 *     src/content/newsletters/posts/*.js 에 파일을 추가하면 자동으로 등록된다.
 *     각 파일은 `meta` 객체와 본문 React 컴포넌트를 default export 한다.
 *
 *  2. Notion (선택)
 *     `npm run newsletter:sync` 가 Notion DB를 읽어
 *     src/content/newsletters/generated/*.json 을 만든다. 파일이 있으면 자동 등록된다.
 *     Notion을 쓰지 않으면 이 폴더는 비어 있고, 아무것도 달라지지 않는다.
 *
 * 같은 slug가 양쪽에 있으면 정적 페이지가 우선한다(손으로 다듬은 쪽이 최종본).
 *
 * 모든 글은 카테고리 하나에 속한다 (categories.js). meta.category 를 비워 두면 회사소식.
 *
 * `pinned: true` 인 글은 목록 맨 위에 고정된다(카테고리 목록 안에서도). 고정은 목록
 * 순서에만 영향을 주고, 상세 페이지의 이전/다음 글과 같은 카테고리 글은 날짜순이다.
 */

import { DEFAULT_CATEGORY, resolveCategory } from "./categories";

/**
 * 목록에 쓸 썸네일. 따로 지정한 `thumbnail` 이 없으면 상세 상단의 `cover` 를 그대로 쓴다.
 * 둘 다 없으면 목록 행은 글자만으로 그려진다.
 */
function thumbnailOf(meta) {
  return meta.thumbnail || meta.cover || null;
}

function loadLocalPosts() {
  const context = require.context("./posts", false, /\.js$/);

  return context.keys().map((key) => {
    const mod = context(key);
    const meta = mod.meta || {};

    if (!meta.slug) {
      throw new Error(
        `뉴스레터 ${key} 에 meta.slug 가 없습니다. meta를 named export 하세요.`
      );
    }

    // 정적 원고는 개발자가 직접 쓰므로 오타를 빌드에서 바로 잡는다.
    const category = resolveCategory(meta.category);
    if (!category) {
      throw new Error(
        `뉴스레터 ${key} 의 meta.category "${meta.category}" 를 모릅니다. ` +
          `src/content/newsletters/categories.js 의 id 를 쓰세요.`
      );
    }

    return {
      ...meta,
      lang: meta.lang || "ko",
      tags: meta.tags || [],
      category,
      pinned: meta.pinned === true,
      thumbnail: thumbnailOf(meta),
      source: "local",
      Body: mod.default,
    };
  });
}

function loadNotionPosts() {
  const context = require.context("./generated", false, /\.json$/);

  return context.keys().map((key) => {
    const data = context(key);

    // Notion 은 사람이 고르는 값이라 모르는 이름이 들어올 수 있다. 사이트를 깨뜨리지
    // 않고 회사소식으로 싣되, 개발 중에는 알려 준다.
    let category = resolveCategory(data.category);
    if (!category) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `[newsletter] ${key} 의 카테고리 "${data.category}" 를 몰라 회사소식으로 싣습니다.`
        );
      }
      category = DEFAULT_CATEGORY;
    }

    return {
      ...data,
      lang: data.lang || "ko",
      tags: data.tags || [],
      category,
      pinned: data.pinned === true,
      thumbnail: thumbnailOf(data),
      source: "notion",
    };
  });
}

function byDateDesc(a, b) {
  return String(b.date).localeCompare(String(a.date));
}

function buildRegistry() {
  const local = loadLocalPosts();
  const localSlugs = new Set(local.map((post) => post.slug));
  const notion = loadNotionPosts().filter((post) => !localSlugs.has(post.slug));

  return [...local, ...notion]
    .filter((post) => post.published !== false)
    .sort(byDateDesc);
}

const registry = buildRegistry();

// 날짜순(최신순). registry 가 이미 날짜순이다.
function byDate(lang, category = null) {
  return registry.filter(
    (post) => post.lang === lang && (!category || post.category === category)
  );
}

/**
 * 해당 언어의 뉴스레터 목록. category 를 주면 그 카테고리만.
 * 고정된 글이 먼저, 그 안에서는 각각 최신순.
 */
export function getNewsletters(lang = "ko", category = null) {
  const list = byDate(lang, category);
  return [
    ...list.filter((post) => post.pinned),
    ...list.filter((post) => !post.pinned),
  ];
}

/** 해당 언어에서 카테고리별 글 수. { company: 3, "student-maker": 1, ... } */
export function getCategoryCounts(lang = "ko") {
  const counts = {};
  for (const post of byDate(lang)) {
    counts[post.category] = (counts[post.category] || 0) + 1;
  }
  return counts;
}

/** 같은 언어·같은 카테고리의 다른 글 (최신순, 최대 limit편). 고정 여부와 무관하다. */
export function getRelated(post, limit = 3) {
  return byDate(post.lang, post.category)
    .filter((other) => other.slug !== post.slug)
    .slice(0, limit);
}

/** slug로 한 편을 찾는다. 언어가 달라도 slug가 맞으면 찾아준다. */
export function getNewsletter(slug, lang = "ko") {
  return (
    registry.find((post) => post.slug === slug && post.lang === lang) ||
    registry.find((post) => post.slug === slug) ||
    null
  );
}

/** 같은 언어 안에서 날짜상 이전/다음 글. 고정 여부와 무관하다. */
export function getAdjacent(slug, lang = "ko") {
  const list = byDate(lang);
  const index = list.findIndex((post) => post.slug === slug);

  if (index === -1) return { newer: null, older: null };

  return {
    newer: index > 0 ? list[index - 1] : null,
    older: index < list.length - 1 ? list[index + 1] : null,
  };
}

export default registry;
